import type { AIModelChoices } from 'api';
import { logger } from 'monitor';
import { useCallback, useEffect, useState } from 'react';

import { isLooseAiModel } from '../../../evidence';
import { TiltDirection } from '../../types';

export type Tilt = {
  alpha: number;
  beta: number;
  gamma: number;
};

const TILT_THRESHOLD = 10;
const UPRIGHT_BETA = 90;
const FLAT_BETA = 0;
const UPRIGHT_GAMMA = -90;
const FLAT_GAMMA = 0;
const UPSIDE_DOWN_BETA = -90;
const UPSIDE_DOWN_GAMMA = 90;

const isWithinThreshold = (value: number, target: number) =>
  value >= target - TILT_THRESHOLD && value <= target + TILT_THRESHOLD;

const getPortraitVerticalLevel = (tilt: Tilt) =>
  isWithinThreshold(tilt.beta, UPRIGHT_BETA) || isWithinThreshold(tilt.beta, UPSIDE_DOWN_BETA);

const getPortraitFlatLevel = (tilt: Tilt) => isWithinThreshold(tilt.beta, FLAT_BETA);

const getLandscapeVerticalLevel = (tilt: Tilt) =>
  isWithinThreshold(tilt.gamma, UPRIGHT_GAMMA) || isWithinThreshold(tilt.gamma, UPSIDE_DOWN_GAMMA);

const getLandscapeFlatLevel = (tilt: Tilt) => isWithinThreshold(tilt.gamma, FLAT_GAMMA);

type CameraTiltStatus = {
  isCameraLevel: boolean;
  tiltDirection: TiltDirection | undefined;
};

const isCameraLookingDown = ({ isPortrait, tilt }: { isPortrait: boolean; tilt: Tilt }): boolean => {
  if (isPortrait) {
    return getPortraitFlatLevel(tilt);
  } else {
    return getLandscapeFlatLevel(tilt);
  }
};

export const getCameraTiltStatus = ({
  isPortrait,
  tilt,
  aiModel,
}: {
  isPortrait: boolean;
  tilt: Tilt;
  aiModel: AIModelChoices;
}): CameraTiltStatus => {
  if (isLooseAiModel(aiModel)) {
    const isLookingDown = isCameraLookingDown({ isPortrait, tilt });
    if (isLookingDown) {
      return { isCameraLevel: true, tiltDirection: undefined };
    } else {
      let tiltDirection: TiltDirection;
      if (isPortrait) {
        tiltDirection = tilt.beta < FLAT_BETA ? TiltDirection.Up : TiltDirection.Down;
      } else {
        tiltDirection = tilt.gamma < FLAT_GAMMA ? TiltDirection.Down : TiltDirection.Up;
      }
      return { isCameraLevel: false, tiltDirection };
    }
  }

  const isCameraLevel = isPortrait
    ? getPortraitVerticalLevel(tilt) || getPortraitFlatLevel(tilt)
    : getLandscapeFlatLevel(tilt) || getLandscapeVerticalLevel(tilt);

  if (isCameraLevel) {
    return { isCameraLevel: true, tiltDirection: undefined };
  }

  let tiltDirection: TiltDirection | undefined;

  if (isPortrait) {
    if (tilt.beta > UPRIGHT_BETA + TILT_THRESHOLD) {
      tiltDirection = TiltDirection.Down;
    } else if (tilt.beta < UPRIGHT_BETA - TILT_THRESHOLD) {
      tiltDirection = TiltDirection.Up;
    }
  } else {
    if (tilt.gamma > UPRIGHT_GAMMA + TILT_THRESHOLD) {
      tiltDirection = TiltDirection.Down;
    } else if (tilt.gamma < UPRIGHT_GAMMA - TILT_THRESHOLD) {
      tiltDirection = TiltDirection.Up;
    }
  }

  return { isCameraLevel: false, tiltDirection };
};

export const useTilt = ({ aiModel }: { aiModel: AIModelChoices }) => {
  const [isPortrait, setIsPortrait] = useState(true);

  const [tilt, setTilt] = useState<Tilt>({
    alpha: 0,
    beta: 0,
    gamma: 0,
  });

  const handleDeviceOrientation = useCallback((event: DeviceOrientationEvent) => {
    setTilt({
      alpha: event.alpha || 0,
      beta: event.beta || 0,
      gamma: event.gamma || 0,
    });
  }, []);

  const checkOrientation = useCallback(() => {
    const isPortraitMode = window.innerHeight > window.innerWidth;
    logger.info('isPortrait', { isPortraitMode });
    setIsPortrait(isPortraitMode);
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    checkOrientation();

    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);

    if (window.DeviceOrientationEvent) {
      window.addEventListener('deviceorientation', handleDeviceOrientation);
    }

    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
      window.removeEventListener('deviceorientation', handleDeviceOrientation);
    };
  }, [handleDeviceOrientation, checkOrientation]);

  const { isCameraLevel, tiltDirection } = getCameraTiltStatus({ aiModel, isPortrait, tilt });

  return {
    isCameraLevel,
    isPortrait,
    tilt,
    tiltDirection,
  };
};
